import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const SUPPORTED_LANGUAGES = ['en', 'fr'];

// fr-CA was dropped as a separate locale. Old bookmarks, saved preferences and values
// already stored server-side may still carry it, so every source is reduced to its base
// language before being trusted.
const normalizeLanguage = (code) => {
  if (!code) return null;
  const base = String(code).split('-')[0];
  return SUPPORTED_LANGUAGES.includes(base) ? base : null;
};
const DEFAULT_LANGUAGE = 'en';
const GEO_CACHE_KEY = 'geo-language';

export const useLanguage = () => {
  const { i18n } = useTranslation();
  const { lang } = useParams();
  const navigate = useNavigate();
  const { user, updateUserPreference } = useAuth();
  const [isLanguageLoaded, setIsLanguageLoaded] = useState(false);

  // Fonction pour détecter la langue du navigateur
  // Renvoie null si le navigateur ne demande ni français ni anglais
  const detectBrowserLanguage = () => {
    const browserLang = navigator.language; // Full language code (e.g., 'fr-CA')
    if (SUPPORTED_LANGUAGES.includes(browserLang)) {
      return browserLang;
    }
    // Fallback to base language (e.g., 'fr' from 'fr-CA')
    const baseLang = browserLang.split('-')[0];
    if (SUPPORTED_LANGUAGES.includes(baseLang)) {
      return baseLang;
    }
    // 'en-GB' and friends: the site is in English, no need to ask the server
    return baseLang === 'en' ? DEFAULT_LANGUAGE : null;
  };

  // Langue suggérée par le pays/la région (Cloudflare), mise en cache pour la session
  const detectGeoLanguage = async () => {
    const cached = sessionStorage.getItem(GEO_CACHE_KEY);
    if (cached && SUPPORTED_LANGUAGES.includes(cached)) {
      return cached;
    }

    try {
      const { data } = await api.get('/geo');
      const geoLang = SUPPORTED_LANGUAGES.includes(data?.language) ? data.language : DEFAULT_LANGUAGE;
      sessionStorage.setItem(GEO_CACHE_KEY, geoLang);
      return geoLang;
    } catch (error) {
      console.warn('Geo language detection failed, falling back to English:', error);
      return DEFAULT_LANGUAGE;
    }
  };

  // Fonction pour déterminer la langue à utiliser
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const determineLanguage = async () => {
    // 1. Langue dans l'URL (priorité la plus haute) — /fr-CA/... retombe sur 'fr'
    const fromUrl = normalizeLanguage(lang);
    if (fromUrl) {
      return fromUrl;
    }

    // 2. Préférence utilisateur connecté
    const fromUser = normalizeLanguage(user?.preferredLanguage);
    if (fromUser) {
      return fromUser;
    }

    // 3. Langue sauvegardée localement
    const fromStorage = normalizeLanguage(localStorage.getItem('preferred-language'));
    if (fromStorage) {
      return fromStorage;
    }

    // 4. Langue du navigateur
    const browserLang = detectBrowserLanguage();
    if (browserLang) {
      return browserLang;
    }

    // 5. Ni français ni anglais demandé: on se rabat sur le pays (Québec, France,
    //    Maghreb, Afrique francophone... -> français, sinon anglais)
    return detectGeoLanguage();
  };

  const navigateToLanguage = (targetLang) => {
    const currentPath = window.location.pathname;
    // fr-CA stays in this pattern (matched first) so old /fr-CA/... URLs are
    // stripped correctly on their way to /fr, rather than leaving a '-CA' behind
    const pathWithoutLang = currentPath.replace(/^\/(fr-CA|fr|en)/, '') || '/';
    // Keep search/hash: entry params like the password-reset token live there
    const suffix = window.location.search + window.location.hash;
    navigate(`/${targetLang}${pathWithoutLang}${suffix}`, { replace: true });
  };

  // Fonction pour changer la langue
  // L'URL est la seule source de vérité: on ne touche pas i18next ici, l'effet
  // d'initialisation (déclenché par le changement de :lang) s'en charge. Changer
  // i18next avant l'URL faisait se contredire les instances du hook (URL /en vs
  // i18n fr) et bouclait jusqu'à faire planter l'onglet.
  const changeLanguage = (newLang) => {
    if (!SUPPORTED_LANGUAGES.includes(newLang)) {
      console.warn(`Language ${newLang} is not supported`);
      return;
    }

    localStorage.setItem('preferred-language', newLang);
    navigateToLanguage(newLang);

    if (user) {
      updateUserPreference('language', newLang).catch((error) => {
        console.warn('Failed to save language preference to server:', error);
      });
    }
  };

  // Initialisation de la langue — ne dépend que de l'URL (et de la préférence
  // utilisateur quand l'URL n'a pas de langue), pas de fonctions recréées à chaque rendu
  useEffect(() => {
    let cancelled = false;

    const initializeLanguage = async () => {
      const targetLanguage = await determineLanguage();
      if (cancelled) return;

      // Si l'URL ne contient pas la bonne langue, rediriger (l'effet se relancera)
      if (lang !== targetLanguage) {
        navigateToLanguage(targetLanguage);
        return;
      }

      if (i18n.language !== targetLanguage) {
        await i18n.changeLanguage(targetLanguage);
        if (cancelled) return;
      }

      setIsLanguageLoaded(true);
    };

    initializeLanguage();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, user?.preferredLanguage]);

  return {
    currentLanguage: i18n.language,
    changeLanguage,
    isLanguageLoaded,
    supportedLanguages: SUPPORTED_LANGUAGES
  };
};
